// First Created - 04 Sep 2026
// Author - Atul Pandey
// Purpose - RTK Query API slice for Trinity Intention Board questions.

import {
  createSelector,
  createEntityAdapter,
} from '@reduxjs/toolkit'

import {
  apiSlice,
} from '@/app/api/apiSlice'

/*
|--------------------------------------------------------------------------
| Entity Adapter
|--------------------------------------------------------------------------
*/

const orgIntentionQuestionsAdapter =
  createEntityAdapter({})

const initialState =
  orgIntentionQuestionsAdapter
    .getInitialState()

/*
|--------------------------------------------------------------------------
| API Slice
|--------------------------------------------------------------------------
*/

export const orgIntentionQuestionsApiSlice =
  apiSlice.injectEndpoints({

    endpoints:
      builder => ({

        /*
        |--------------------------------------------------------------------------
        | Get Intention Questions
        |--------------------------------------------------------------------------
        */

        getOrgIntentionQuestions:
          builder.query({

            query:
              ({
                intentionBoardId,
              }) => ({

                url:
                  '/orgIntentionQuestion/getOrgIntentionQuestions',

                params: {
                  intentionBoardId,
                },

                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),

            transformResponse:
              responseData => {

                const loadedOrgIntentionQuestions =
                  responseData.map(
                    intentionQuestion => {

                      intentionQuestion.id =
                        intentionQuestion._id

                      return intentionQuestion

                    }
                  )

                return orgIntentionQuestionsAdapter
                  .setAll(
                    initialState,
                    loadedOrgIntentionQuestions
                  )

              },

            providesTags:
              (
                result,
                error,
                arg
              ) => {

                if (
                  result?.ids
                ) {

                  return [

                    {
                      type:
                        'OrgIntentionQuestion',

                      id:
                        `LIST-${arg?.intentionBoardId}`,
                    },

                    ...result.ids.map(
                      id => ({

                        type:
                          'OrgIntentionQuestion',

                        id,

                      })
                    ),

                  ]

                }

                return [

                  {
                    type:
                      'OrgIntentionQuestion',

                    id:
                      `LIST-${arg?.intentionBoardId}`,
                  },

                ]

              },

          }),

        /*
        |--------------------------------------------------------------------------
        | Get Intention Question By Id
        |--------------------------------------------------------------------------
        */

        getOrgIntentionQuestionById:
          builder.query({

            query:
              ({
                intentionQuestionId,
              }) => ({

                url:
                  '/orgIntentionQuestion/getOrgIntentionQuestionById',

                params: {
                  intentionQuestionId,
                },

                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),

            providesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type:
                    'OrgIntentionQuestion',

                  id:
                    arg
                      ?.intentionQuestionId,
                },

              ],

          }),

        /*
        |--------------------------------------------------------------------------
        | Create Intention Question
        |--------------------------------------------------------------------------
        */

        createNewOrgIntentionQuestion:
          builder.mutation({

            query:
              initialOrgIntentionQuestion => ({

                url:
                  '/orgIntentionQuestion/createNewOrgIntentionQuestion',

                method:
                  'POST',

                body: {
                  ...initialOrgIntentionQuestion,
                },

              }),

            invalidatesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type:
                    'OrgIntentionQuestion',

                  id:
                    `LIST-${arg?.intentionBoardId}`,
                },

              ],

          }),

        /*
        |--------------------------------------------------------------------------
        | Update Intention Question
        |--------------------------------------------------------------------------
        */

        updateOrgIntentionQuestion:
          builder.mutation({

            query:
              initialOrgIntentionQuestion => ({

                url:
                  '/orgIntentionQuestion/updateOrgIntentionQuestion',

                method:
                  'PATCH',

                body: {
                  ...initialOrgIntentionQuestion,
                },

              }),

            invalidatesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type:
                    'OrgIntentionQuestion',

                  id:
                    arg
                      ?.intentionQuestionId,
                },

                {
                  type:
                    'OrgIntentionQuestion',

                  id:
                    `LIST-${arg?.intentionBoardId}`,
                },

              ],

          }),

        /*
        |--------------------------------------------------------------------------
        | Delete Intention Question
        |--------------------------------------------------------------------------
        */

        deleteOrgIntentionQuestion:
          builder.mutation({

            query:
              ({
                intentionQuestionId,
              }) => ({

                url:
                  '/orgIntentionQuestion/deleteOrgIntentionQuestion',

                method:
                  'DELETE',

                body: {
                  intentionQuestionId,
                },

              }),

            invalidatesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type:
                    'OrgIntentionQuestion',

                  id:
                    arg
                      ?.intentionQuestionId,
                },

                {
                  type:
                    'OrgIntentionQuestion',

                  id:
                    `LIST-${arg?.intentionBoardId}`,
                },

              ],

          }),

      }),

  })

/*
|--------------------------------------------------------------------------
| Hooks
|--------------------------------------------------------------------------
*/

export const {

  useGetOrgIntentionQuestionsQuery,

  useGetOrgIntentionQuestionByIdQuery,

  useCreateNewOrgIntentionQuestionMutation,

  useUpdateOrgIntentionQuestionMutation,

  useDeleteOrgIntentionQuestionMutation,

} =
  orgIntentionQuestionsApiSlice

/*
|--------------------------------------------------------------------------
| Query Result Selector
|--------------------------------------------------------------------------
|
| Because this query requires intentionBoardId, selectors should generally
| be created with the board id as an argument.
|
*/

export const selectOrgIntentionQuestionsResult =
  intentionBoardId =>
    orgIntentionQuestionsApiSlice
      .endpoints
      .getOrgIntentionQuestions
      .select({
        intentionBoardId,
      })

/*
|--------------------------------------------------------------------------
| Questions Data Selector
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionQuestionsData =
  intentionBoardId =>
    createSelector(

      selectOrgIntentionQuestionsResult(
        intentionBoardId
      ),

      orgIntentionQuestionsResult =>
        orgIntentionQuestionsResult
          .data

    )

/*
|--------------------------------------------------------------------------
| Entity Selectors
|--------------------------------------------------------------------------
|
| Usage:
|
| const selectors =
|   selectOrgIntentionQuestionSelectors(
|     intentionBoardId
|   )
|
*/

export const selectOrgIntentionQuestionSelectors =
  intentionBoardId =>
    orgIntentionQuestionsAdapter
      .getSelectors(

        state => {

          const questionsData =
            selectOrgIntentionQuestionsData(
              intentionBoardId
            )(
              state
            )

          return (
            questionsData ??
            initialState
          )

        }

      )